import React, { useEffect, useState } from "react";
import { Offcanvas, CloseButton, Form } from "react-bootstrap";
import { Button as ButtonAntd, Input } from "antd";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSave, faEdit } from "@fortawesome/free-solid-svg-icons";
import { toast } from "react-toastify";

const { TextArea } = Input;

export default function FeatureDialDrawer({
  dial,
  show,
  onClose,
  onSave,
  isSubmitting
}) {
  const [visible, setVisible] = useState(false);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({});
  const [nameError, setNameError] = useState("");

  const isNew = !dial;

  useEffect(() => {
    if (show) setVisible(true);
    if (!show) setVisible(false);
  }, [show]);

  useEffect(() => {
    if (!show) return;

    const base = dial
      ? {
          id: dial.id,
          name: dial.name || "",
          description: dial.description || "",
          oliveebApp: dial.oliveebApp || "olitask-main-service",
          isOn: !!dial.isOn
        }
      : {
          name: "",
          description: "",
          oliveebApp: "olitask-main-service",
          isOn: false
        };

    setForm(base);
    setEditing(isNew);
    setNameError("");
  }, [show, dial]);

  const update = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const handleNameChange = (value) => {
    const raw = value || "";
    const hasSpace = /\s/.test(raw);
    const upper = raw.replace(/\s+/g, "").toUpperCase();
    update("name", upper);

    if (hasSpace) {
      setNameError("Spaces are not allowed in feature names.");
      return;
    }

    setNameError("");
  };

  const handleSave = () => {
    if (!form.name?.trim()) {
      toast.error("Feature name required");
      setNameError("Feature name is required.");
      return;
    }

    if (nameError) {
      toast.error("Please fix the feature name.");
      return;
    }

    onSave({
      name: form.name,
      description: form.description,
      oliveebApp: form.oliveebApp,
      isOn: form.isOn
    });
  };

  const handleClose = () => {
    setVisible(false);
    setTimeout(onClose, 300);
  };

  return (
    <Offcanvas
      show={visible}
      onHide={handleClose}
      placement="end"
      backdrop="static"
      style={{ width: "34rem" }}
      className="border-0 shadow"
    >
      {/* HEADER */}

      <div className="offcanvas-header border-bottom px-4 py-3">
        <div>
          <div className="text-900 fw-bold fs-10">
            {isNew ? "Register Feature Dial" : "Feature Dial"}
          </div>
          <div className="text-500 fs-11">
            Manage feature toggle configuration
          </div>
        </div>

        <CloseButton onClick={handleClose} />
      </div>

      <div className="offcanvas-body px-4 py-3">

        {/* HERO FEATURE CARD */}

        <div
          className="p-3 rounded-3 mb-4 bg-light border"
          style={{ borderColor: "#edf2f9" }}
        >
          <div className="d-flex justify-content-between align-items-center">

            <div>

              <div className="text-900 fs-10 fw-semibold mb-1">
                {form.name || "Unnamed Feature"}
              </div>

              <div className="d-flex gap-2 align-items-center">

                <span
                  className={`badge rounded-pill fs-11 ${
                    form.isOn
                      ? "badge-subtle-success"
                      : "badge-subtle-secondary"
                  }`}
                >
                  {form.isOn ? "ACTIVE" : "OFF"}
                </span>

              </div>

            </div>

            <Form.Check
              type="switch"
              checked={!!form.isOn}
              disabled={!editing}
              onChange={(e) => update("isOn", e.target.checked)}
            />

          </div>
        </div>

        {/* CONFIGURATION */}

        <div className="mb-4">

          <div className="text-500 fs-11 fw-bold mb-2">
            CONFIGURATION
          </div>

          <div className="d-flex flex-column gap-3">

            <div>
              <div className="text-500 fs-11 mb-1">Feature Name</div>

              <Input
                disabled={!editing}
                value={form.name}
                placeholder="ENABLE_BETA_PAYMENTS"
                onChange={(e) => handleNameChange(e.target.value)}
                status={nameError ? "error" : undefined}
              />
              {nameError && (
                <div style={{ marginTop: 4, fontSize: 11, color: "#dc2626" }}>
                  {nameError}
                </div>
              )}
            </div>

          </div>
        </div>

        {/* DESCRIPTION */}

        <div>

          <div className="text-500 fs-11 fw-bold mb-2">
            DESCRIPTION
          </div>

          <TextArea
            rows={4}
            disabled={!editing}
            placeholder="What does this feature dial control?"
            value={form.description}
            onChange={(e) => update("description", e.target.value)}
          />

        </div>
      </div>

      {/* FOOTER */}

      <div className="border-top px-4 py-3 d-flex justify-content-between align-items-center">

        <small className="text-500 fs-11">
          {dial?.id && `ID: ${dial.id}`}
        </small>

        <div className="d-flex gap-2">

          {editing ? (
            <>
              <ButtonAntd
                onClick={() => {
                  setEditing(false);
                  handleClose();
                }}
              >
                Cancel
              </ButtonAntd>

              <ButtonAntd
                type="primary"
                icon={<FontAwesomeIcon icon={faSave} />}
                loading={isSubmitting}
                onClick={handleSave}
              >
                Save
              </ButtonAntd>
            </>
          ) : (
            <ButtonAntd
              type="primary"
              icon={<FontAwesomeIcon icon={faEdit} />}
              onClick={() => setEditing(true)}
            >
              Edit
            </ButtonAntd>
          )}

        </div>
      </div>
    </Offcanvas>
  );
}
